import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import {
    getInstance
} from "../auth/index";
import mandatoryAssignments from "./mandatoryAssignments";

Vue.use(Vuex)

export default new Vuex.Store({
    state: {
        authToken: null,
        // baseApiUrl: 'https://localhost:44313/api',
        baseApiUrl: 'https://portal-api.getacademy.no/api',
        userInfo: {},

        searchOptions: {
            Kodekompetanser: ["C# 1", "C# 2", "C# 3", "SQL 1", "SQL 2", "SQL 3"],
            Nøkkelkompetanser: ["Grit", "Growth mindset", "Honesty"],
            Bosted: ["Larvik", "Sandefjord", "Tønsberg", "Porsgrunn", "Skien"],
        }
    },

    getters: {
        axiosConfig: state => ({
            headers: {
                // Authrization trenger en authToken
                Authorization: `Bearer ${state.authToken}`,
            },
        }),
    },

    mutations: {
        authToken: (state, authToken) => state.authToken = authToken,
        userInfo: (state, userInfo) => state.userInfo = userInfo,
        userPortfolio: (state, portfolio) => state.userInfo.portfolio = portfolio,
        userInterests: (state, interests) => state.userInfo.interests = interests,
        addUserInterest: (state, interests) => state.userInfo.interests.push(interests),
        allCandidates: (state, candidates) => state.allCandidates = candidates,
    },

    actions: {
        /*
         * getUserInfo prøver å hente en Candidate fra API-et.
         * Hvis vellykket:
         *  - lagrer den informasjonen i Store
         *  - returnerer "true"
         * Hvis ikke:
         *  - returnerer "false"
         * Hvis den støter på problemer:
         * - throw new Error(error) tilbake til der den blei kalt fra.
         */
        async getUserInfo({ state, commit, getters, dispatch }, id) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');
                let isFound = false;

                let endpoint = `${state.baseApiUrl}/Candidate`;
                if (id.startsWith("auth0|")) endpoint += `/auth0/${id}`;
                else endpoint += `/${id}`;

                await axios
                    .get(
                        endpoint,
                        getters.axiosConfig
                    )
                    .then((response) => {
                        // 204 betyr at brukeren ikke finnes i databasen.
                        if (response.status === 204) {
                            isFound = false;
                        } else
                            isFound = true;
                        commit('userInfo', response.data);
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });
                return isFound;
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async setUserInfo({ state, commit, getters, dispatch }, patchData) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                await axios
                    .patch(
                        `${state.baseApiUrl}/Candidate`,
                        patchData,
                        getters.axiosConfig
                    )
                    .then((response) => {
                        commit('userInfo', response.data);
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });

        },

        async getUserInterests({ state, commit, getters, dispatch }) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                await axios
                    .get(
                        `${state.baseApiUrl}/Candidate/${state.userInfo.id}/interest`,
                        getters.axiosConfig
                    )
                    .then((response) => {
                        commit('userInterests', response.data);
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });

                return true;
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async addUserInterests({ state, getters, dispatch }, interestId) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                await axios
                    .post(
                        `${state.baseApiUrl}/Candidate/${state.userInfo.id}/interest/${interestId}`,
                        null,
                        getters.axiosConfig
                    )
                    .then(() => {
                        dispatch("getUserInterests");
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async deleteUserInterests({ state, getters, dispatch }, interestId) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                await axios
                    .delete(
                        `${state.baseApiUrl}/Candidate/${state.userInfo.id}/interest/${interestId}`,
                        getters.axiosConfig
                    )
                    .then(() => {
                        dispatch("getUserInterests");
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async getUserPortfolio({ state, commit, getters, dispatch }) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                await axios
                    .get(
                        `${state.baseApiUrl}/Candidate/${state.userInfo.id}/portfolio`,
                        getters.axiosConfig
                    )
                    .then((response) => {
                        commit('userPortfolio', response.data);
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });

                return true;
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async getUserPortfolioProject({ state, getters, dispatch }, projectId) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                return await axios
                    .get(
                        `${state.baseApiUrl}/Candidate/portfolio/${projectId}`,
                        getters.axiosConfig
                    )
                    .then((response) => {
                        return response.data;
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async addUserPortfolioProject({ state, getters, dispatch }, project) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                await axios
                    .post(
                        `${state.baseApiUrl}/Candidate/portfolio`,
                        project,
                        getters.axiosConfig
                    )
                    .then(() => {
                        dispatch("getUserPortfolio");
                    })
                    .catch(error => {
                        throw new Error(error);
                    });
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async updateUserPortfolioProject({ state, getters, dispatch }, project) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                await axios
                    .patch(
                        `${state.baseApiUrl}/Candidate/portfolio`,
                        project,
                        getters.axiosConfig
                    )
                    .then(() => {
                        dispatch("getUserPortfolio");
                    })
                    .catch(error => {
                        throw new Error(error);
                    });
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async deleteUserPortfolioProject({ state, getters, dispatch }, projectId) {
            const authService = getInstance();

            async function fn() {
                await dispatch('getToken');

                await axios
                    .delete(
                        `${state.baseApiUrl}/Candidate/portfolio/${projectId}`,
                        getters.axiosConfig
                    )
                    .then(() => {
                        dispatch("getUserPortfolio");
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },

        async getCandidates({ state, getters, dispatch, commit, }) {
            const authService = getInstance();
            async function fn() {
                await dispatch('getToken');

                await axios
                    .get(
                        `${state.baseApiUrl}/Candidate/`,
                        getters.axiosConfig
                    )
                    .then((response) => {
                        console.log(response.data);
                        commit("allCandidates", response.data);
                        // dispatch("getCandidates");
                    })
                    .catch((error) => {
                        throw new Error(error);
                    });
            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });

        },

        /*
         * Henter ut Auth0 token på foreløpig best mulig måte uten å få:
         * TypeError: Cannot read property 'getTokenSilently' of null
         */
        getToken({ commit }) {
            const authService = getInstance();

            async function fn() {
                if (authService.isAuthenticated) {
                    await authService.getTokenSilently()
                        .then(authToken => {
                            commit("authToken", authToken);
                        })
                        .catch(error => {
                            throw new Error(error);
                        });
                }

            }

            // If loading has already finished, check our auth state using `fn()`
            if (!authService.loading) {
                return fn();
            }

            // Watch for the loading property to change before we check isAuthenticated
            authService.$watch("loading", loading => {
                if (loading === false) {
                    return fn();
                }
            });
        },
    },
    modules: {
        mandatoryAssignments,
    },

})